import { useCallback, useEffect, useRef } from "react";
import type RTKClient from "@cloudflare/realtimekit";

const RECORDING_CHUNK_MS = 5000;

function getHostMediaRecordingMimeType({
  preferWebm = false,
}: {
  preferWebm?: boolean;
} = {}): string {
  const webmCandidates = [
    "video/webm;codecs=h264,opus",
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
  ];
  const mp4Candidates = [
    "video/mp4;codecs=hev1.1.6.L93.B0,mp4a.40.2",
    "video/mp4;codecs=hvc1.1.6.L93.B0,mp4a.40.2",
    "video/mp4;codecs=avc1.640028,mp4a.40.2",
    "video/mp4",
  ];
  const candidates = preferWebm
    ? [...webmCandidates, ...mp4Candidates]
    : [...mp4Candidates, ...webmCandidates];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) ?? "";
}

function getRecordingFileExtension(mimeType: string): string {
  return mimeType.includes("mp4") ? "mp4" : "webm";
}

function getRecordingFileBaseName({ productName }: { productName?: string }): string {
  const trimmed = productName?.trim();
  if (!trimmed) return "host-recording";

  const slug = trimmed
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9_-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  return slug || "host-recording";
}

function getRecordingFileName({
  productName,
  mimeType,
}: {
  productName?: string;
  mimeType: string;
}): string {
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
  const extension = getRecordingFileExtension(mimeType);
  return `${getRecordingFileBaseName({ productName })}-${timestamp}.${extension}`;
}

function getRecordingFilePickerTypes(mimeType: string) {
  const extension = getRecordingFileExtension(mimeType);
  const mimeBase = mimeType.split(";")[0] ?? mimeType;
  return [
    {
      description: "Video File",
      accept: { [mimeBase]: [`.${extension}`] },
    },
  ];
}

function supportsFileSystemRecording(): boolean {
  return "showSaveFilePicker" in window;
}

async function requestRecordingWritableStream({
  mimeType,
  productName,
}: {
  mimeType: string;
  productName?: string;
}): Promise<FileSystemWritableFileStream | null> {
  if (!supportsFileSystemRecording()) return null;

  try {
    const handle = await window.showSaveFilePicker({
      suggestedName: getRecordingFileName({ productName, mimeType }),
      types: getRecordingFilePickerTypes(mimeType),
    });
    return await handle.createWritable();
  } catch {
    return null;
  }
}

function downloadRecordingBlob({
  blob,
  mimeType,
  productName,
}: {
  blob: Blob;
  mimeType: string;
  productName?: string;
}) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = getRecordingFileName({ productName, mimeType });
  anchor.click();
  URL.revokeObjectURL(url);
}

function buildRecordingStream({
  videoTrack,
  audioTrack,
}: {
  videoTrack: MediaStreamTrack | null;
  audioTrack: MediaStreamTrack | null;
}): MediaStream | null {
  const tracks: MediaStreamTrack[] = [];
  if (videoTrack) tracks.push(videoTrack);
  if (audioTrack) tracks.push(audioTrack);
  if (tracks.length === 0) return null;
  return new MediaStream(tracks);
}

export function useHostMediaLocalRecording({
  meeting,
  videoTrack,
  audioTrack,
  productName,
}: {
  meeting: RTKClient | undefined;
  videoTrack: MediaStreamTrack | null;
  audioTrack: MediaStreamTrack | null;
  productName?: string;
}) {
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const writableStreamRef = useRef<FileSystemWritableFileStream | null>(null);
  const writeChainRef = useRef<Promise<void>>(Promise.resolve());
  const usingFileSystemRef = useRef(false);
  const preparedMimeTypeRef = useRef<string | null>(null);
  const productNameRef = useRef(productName);

  useEffect(() => {
    productNameRef.current = productName;
  }, [productName]);

  const clearPreparedRecordingDestination = useCallback(async () => {
    if (!writableStreamRef.current || recorderRef.current) return;

    await writeChainRef.current.catch(() => undefined);
    await writableStreamRef.current.close().catch(() => undefined);
    writableStreamRef.current = null;
    usingFileSystemRef.current = false;
    preparedMimeTypeRef.current = null;
    writeChainRef.current = Promise.resolve();
  }, []);

  const prepareRecordingDestination = useCallback(async (): Promise<boolean> => {
    if (!meeting) return false;

    await clearPreparedRecordingDestination();

    if (!supportsFileSystemRecording()) return true;

    const mimeType = getHostMediaRecordingMimeType({ preferWebm: true });
    if (!mimeType) return false;

    const writableStream = await requestRecordingWritableStream({
      mimeType,
      productName: productNameRef.current,
    });
    if (!writableStream) return false;

    preparedMimeTypeRef.current = mimeType;
    writableStreamRef.current = writableStream;
    usingFileSystemRef.current = true;
    return true;
  }, [clearPreparedRecordingDestination, meeting]);

  const startHostRecording = useCallback(
    async ({
      videoTrack: videoTrackOverride,
      audioTrack: audioTrackOverride,
    }: {
      videoTrack?: MediaStreamTrack | null;
      audioTrack?: MediaStreamTrack | null;
    } = {}) => {
      if (!meeting || recorderRef.current) return false;

      const useFileSystem = supportsFileSystemRecording();
      const preparedMimeType = preparedMimeTypeRef.current;
      const mimeType =
        preparedMimeType ??
        getHostMediaRecordingMimeType({ preferWebm: useFileSystem });
      if (!mimeType) {
        await clearPreparedRecordingDestination();
        return false;
      }

      const stream = buildRecordingStream({
        videoTrack: videoTrackOverride ?? videoTrack,
        audioTrack: audioTrackOverride ?? audioTrack,
      });
      if (!stream) {
        await clearPreparedRecordingDestination();
        return false;
      }

      if (useFileSystem && !writableStreamRef.current) {
        await clearPreparedRecordingDestination();
        return false;
      }

      chunksRef.current = [];
      writeChainRef.current = Promise.resolve();

      const recorder = new MediaRecorder(stream, { mimeType });
      recorder.ondataavailable = (event) => {
        if (event.data.size <= 0) return;

        if (writableStreamRef.current) {
          writeChainRef.current = writeChainRef.current.then(() =>
            writableStreamRef.current!.write(event.data),
          );
          return;
        }

        chunksRef.current.push(event.data);
      };
      recorder.start(RECORDING_CHUNK_MS);
      recorderRef.current = recorder;
      preparedMimeTypeRef.current = null;
      return true;
    },
    [clearPreparedRecordingDestination, meeting, videoTrack, audioTrack],
  );

  const closeHostRecordingStream = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder) return;

    recorderRef.current = null;

    recorder.onstop = async () => {
      const mimeType = recorder.mimeType || "video/webm";

      if (usingFileSystemRef.current && writableStreamRef.current) {
        await writeChainRef.current;
        await writableStreamRef.current.close();
        writableStreamRef.current = null;
        usingFileSystemRef.current = false;
        return;
      }

      const blob = new Blob(chunksRef.current, { type: mimeType });
      chunksRef.current = [];
      if (blob.size > 0) {
        downloadRecordingBlob({
          blob,
          mimeType,
          productName: productNameRef.current,
        });
      }
    };
    recorder.stop();
  }, []);

  const stopHostRecording = useCallback(() => {
    closeHostRecordingStream();
  }, [closeHostRecordingStream]);

  useEffect(() => {
    return () => {
      closeHostRecordingStream();
      chunksRef.current = [];
      writeChainRef.current = Promise.resolve();
    };
  }, [closeHostRecordingStream]);

  return {
    prepareRecordingDestination,
    startHostRecording,
    stopHostRecording,
  };
}
